import type {
  IFilterDateRange,
  IHeatmapBaseFilterPayload,
  IHeatmapDraftBaseFilterPayload,
} from '../types/filter';
import type { IHeatmapDraftFilterPayload } from '../types/filter-interfaces/filter-payload';
import type { ICampaignTemplate } from '~/modules/campaign-template/types';

import { EHeatmapFilterType, EHeatmapBaseFilterType, EJourneyMatchType } from '../types/filter';
import { getDateTimeFilterBase } from '~/presentation/ui';
import { dayjsTzToLocalTZ } from '~/utils/dayjs';
import { deepCopy } from '~/utils';

type FilterProcessor = (draft: IHeatmapDraftFilterPayload) => IHeatmapDraftFilterPayload;

const processVisitorJourneyFilter: FilterProcessor = (draft) => {
  const journeyFilter = draft[EHeatmapFilterType.VISITOR_JOURNEY];
  if (!journeyFilter?.value) return draft;

  const exitPageIndex = journeyFilter.value.findIndex(
    (step) => step.subFilter?.matchType === EJourneyMatchType.EXACT,
  );
  if (exitPageIndex === -1) return draft;

  return {
    ...draft,
    [EHeatmapFilterType.VISITOR_JOURNEY]: {
      value: journeyFilter.value.slice(0, exitPageIndex + 1),
    },
  };
};

const filterProcessors: Partial<Record<EHeatmapFilterType, FilterProcessor>> = {
  [EHeatmapFilterType.VISITOR_JOURNEY]: processVisitorJourneyFilter,
};

export function applyDraftFilterProcessors(
  draft: IHeatmapDraftFilterPayload,
): IHeatmapDraftFilterPayload {
  let processed = { ...draft };

  for (const [filterType, processor] of Object.entries(filterProcessors)) {
    if (draft[filterType as EHeatmapFilterType] && processor) {
      processed = processor(processed);
    }
  }

  return processed;
}

// ===== Base filter processors =====

export interface IBaseFilterProcessorContext {
  campaigns: ICampaignTemplate[];
  campaignGroups: ICampaignTemplate[];
}

export interface IBaseFilterProcessorOptions {
  draftFilters: IHeatmapDraftFilterPayload;
  baseFilters: IHeatmapBaseFilterPayload;
  draftBaseFilters: IHeatmapDraftBaseFilterPayload;
}

type IDraftBaseFilterProcessor = (
  ctx: IBaseFilterProcessorContext,
  options: IBaseFilterProcessorOptions,
) => IHeatmapDraftBaseFilterPayload;

const processCampaignDateRange: IDraftBaseFilterProcessor = (ctx, options) => {
  const { draftFilters, baseFilters, draftBaseFilters } = options;

  const draftDateRange = draftBaseFilters[EHeatmapBaseFilterType.DATE_RANGE]?.value;
  const baseDateRange = baseFilters[EHeatmapBaseFilterType.DATE_RANGE]?.value;

  const dateRange = draftDateRange ?? baseDateRange;
  if (!dateRange?.isVersion) return draftBaseFilters;

  const campaignFilter = draftFilters[EHeatmapFilterType.CAMPAIGN]?.value;

  const selectedCampaignGroup = campaignFilter?.campaignGroupId
    ? ctx.campaignGroups.find((c) => c.id === campaignFilter.campaignGroupId)
    : undefined;
  const selectedCampaign = campaignFilter?.campaignId
    ? ctx.campaigns.find((c) => c.id === campaignFilter.campaignId)
    : undefined;

  const versions = selectedCampaignGroup?.versions ?? selectedCampaign?.versions;
  const versionMatch = versions?.some((v) => v?.id === dateRange.versionId);
  if (versionMatch) return draftBaseFilters;

  const since = dayjsTzToLocalTZ(dateRange.since).startOf('day').valueOf();
  const until = dayjsTzToLocalTZ(dateRange.until).endOf('day').valueOf();

  const { title, alias } = getDateTimeFilterBase(since, until);
  const newDateRange: IFilterDateRange['value'] = { alias, title, since, until };

  return {
    ...draftBaseFilters,
    [EHeatmapBaseFilterType.DATE_RANGE]: {
      value: newDateRange,
    },
  };
};

const baseFilterProcessors: Partial<Record<EHeatmapBaseFilterType, IDraftBaseFilterProcessor>> = {
  [EHeatmapBaseFilterType.DATE_RANGE]: processCampaignDateRange,
};

export function applyDraftBaseFilterProcessors(
  ctx: IBaseFilterProcessorContext,
  options: IBaseFilterProcessorOptions,
): IHeatmapDraftBaseFilterPayload {
  const { draftBaseFilters } = options;

  let processed = deepCopy(draftBaseFilters);
  for (const [, processor] of Object.entries(baseFilterProcessors)) {
    if (processor) {
      processed = processor(ctx, options);
    }
  }

  return processed;
}
